import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';

import {reverse} from 'utils/urls';
import {formatDateForApi, formatDate, DATE_FORMAT_API} from 'utils/datetime';

import {BOOKING_SET_DETAIL_URL} from 'config/urls';

import Table from 'components/Table';
import Button from 'components/Button';
import Loading from 'components/Loading';
import Tooltip from 'components/Tooltip';
import NotesCount from 'components/NotesCount';
import BooleanIcon from 'components/BooleanIcon';
import BookingStatus from 'components/BookingStatus';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';

import {MemberCheckInModal} from './components';
import {
  EmptyListPlaceholder,
  Owed,
  BookingSetBalance
} from 'containers/FrontDesk/components/shared';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {exportCsv, selectExporting, exportCsvInBackground} from 'ducks/files';
import {
  MODALS,
  fetchArrivals,
  selectArrivals,
  selectArrivalsLoading,
  fetchCheckedInTodayArrivals,
  selectCheckedInTodayArrivals,
  selectCheckedInTodayArrivalsLoading,
  undoCheckInTenants
} from 'ducks/frontDesk';
import {
  exportArrivalsCsv as exportArrivalsCsvSdk,
  exportArrivalsCsvInBackground as exportArrivalsCsvInBackgroundSdk
} from 'sdk/frontDesk';

import {
  isModalOpen,
  openModal,
  closeModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';

import {
  TableSplitter,
  BookingType,
  TenantCell
} from 'containers/FrontDesk/components/shared';

import './styles.css';

const ARRIVALS_CSV_FILENAME = 'arrivals';
const TOOLTIP_NAME = 'arrivalsTableTooltip';

const ArrivalRow = ({
  data: {
    bookingId,
    bookingSetId,
    tenancyId,
    primaryTenant,
    secondaryTenant,
    room,
    roomType,
    roomInspectionPassed,
    status,
    bookingType,
    agreementSigned,
    arrivalDate,
    departureDate,
    notesCount,
    hasUnresolvedNotes,
    bookingSetBalance,
    bookingSetRentAndOneOffBalance,
    bookingSetDepositBalance
  },
  openMemberCheckInModal,
  openRoomInspectionModal,
  openBookingNotesModal,
  isToday,
  redirectToBookingSet,
  undoCheckInTenants
}: any) => (
  <Table.Row onClick={() => redirectToBookingSet({bookingSetId})}>
    <Table.Cell className="name">
      <TenantCell
        primaryTenant={primaryTenant}
        secondaryTenant={secondaryTenant}
      />
    </Table.Cell>
    <Table.Cell className="type">
      <div
        data-tip={JSON.stringify({type: 'bookingType', content: bookingType})}
        data-for={TOOLTIP_NAME}>
        <BookingType bookingType={bookingType} />
      </div>
    </Table.Cell>
    <Table.Cell className="arrival">{formatDate(arrivalDate)}</Table.Cell>
    <Table.Cell className="departure">{formatDate(departureDate)}</Table.Cell>
    <Table.Cell className="room">{room}</Table.Cell>
    <Table.Cell className="roomType">{roomType}</Table.Cell>
    <Table.Cell className="balance">
      <div
        data-tip={JSON.stringify({
          bookingSetRentAndOneOffBalance,
          bookingSetDepositBalance
        })}
        data-for={TOOLTIP_NAME}>
        <Owed amount={bookingSetBalance} />
      </div>
    </Table.Cell>
    <Table.Cell className="notesCount">
      <NotesCount
        notesCount={notesCount}
        showNotification={hasUnresolvedNotes}
        onClick={() => openBookingNotesModal(bookingId)}
      />
    </Table.Cell>
    <Table.Cell className="inspected">
      <ClickIconWithNoPropagation
        onClick={() => openRoomInspectionModal(bookingId)}>
        <BooleanIcon value={roomInspectionPassed} />
      </ClickIconWithNoPropagation>
    </Table.Cell>
    <Table.Cell className="agreementSigned">
      <BooleanIcon value={agreementSigned} />
    </Table.Cell>
    <Table.Cell className="status">
      <BookingStatus status={status} />
    </Table.Cell>
    <Table.Cell className="checkIn">
      {openMemberCheckInModal && (
        <ClickIconWithNoPropagation
          onClick={() => openMemberCheckInModal(bookingId, tenancyId)}>
          <Button white disabled={!isToday}>
            Check-in
          </Button>
        </ClickIconWithNoPropagation>
      )}
      {undoCheckInTenants && (
        <ClickIconWithNoPropagation
          onClick={() => undoCheckInTenants(bookingId)}>
          <Button className="checkIn__undo" white>
            Undo check in
          </Button>
        </ClickIconWithNoPropagation>
      )}
    </Table.Cell>
  </Table.Row>
);

interface ArrivalsTableProps {
  filter: {[key: string]: any};
  locationRef: string;
}

class ArrivalsTable extends React.Component<
  ArrivalsTableProps &
    WithSortingProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  debouncer: any = _.debounce(this.rebuildTooltips, 1000);
  state = {
    isOpened: false
  };

  getOption = () => this.props.filter.value;

  isToday = () => this.props.filter.value === 'today';

  hideCheckedIn = () => {
    this.setState({isOpened: false});
  };

  showCheckedIn = () => {
    this.setState({isOpened: true}, this.fetchCheckedInTodayArrivals);
  };

  fetchArrivalsExtraActions = () => {
    const {locationRef, filter: {dateFrom, dateTo}} = this.props;
    return [
      () =>
        fetchArrivals({
          locationRef,
          option: this.getOption(),
          dateFrom: formatDateForApi(dateFrom),
          dateTo: formatDateForApi(dateTo)
        }),
      () => fetchCheckedInTodayArrivals({locationRef})
    ];
  };

  openMemberCheckInModal = (bookingId, tenancyId) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHECK_IN,
        locationRef
      },
      () => {
        this.props.openModal(
          MODALS.MEMBER_CHECK_IN,
          {
            locationRef,
            bookingId,
            tenancyId
          },
          true
        );
      }
    );
  };

  openRoomInspectionPassModal = bookingId => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_ROOM_INSPECTION, locationRef},
      () =>
        this.props.openModal(MODALS.ROOM_INSPECTION_PASS, {
          bookingId,
          locationRef: this.props.locationRef,
          confirmExtraActions: this.fetchArrivalsExtraActions()
        })
    );
  };

  openRoomInspectionRemoveModal = (bookingId: any) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_ROOM_INSPECTION, locationRef},
      () =>
        this.props.openModal(MODALS.ROOM_INSPECTION_REMOVE, {
          bookingId,
          locationRef: this.props.locationRef,
          confirmExtraActions: this.fetchArrivalsExtraActions()
        })
    );
  };

  fetchArrivals = () => {
    return this.props.fetchArrivals({
      locationRef: this.props.locationRef,
      option: this.getOption(),
      dateFrom: formatDateForApi(this.props.filter.dateFrom),
      dateTo: formatDateForApi(this.props.filter.dateTo)
    });
  };

  fetchCheckedInTodayArrivals = () => {
    // Don't refetch checked-in today arrivals if they are already present
    if (_.isEmpty(this.props.checkedInTodayArrivals)) {
      this.props.fetchCheckedInTodayArrivals({
        locationRef: this.props.locationRef
      });
    }
  };

  exportArrivalsCsv = () => {
    this.props.exportCsv({
      exportData: {
        locationRef: this.props.locationRef,
        option: this.getOption(),
        dateFrom: formatDateForApi(this.props.filter.dateFrom),
        dateTo: formatDateForApi(this.props.filter.dateTo)
      },
      exportSdk: exportArrivalsCsvSdk,
      fileName: ARRIVALS_CSV_FILENAME
    });
  };

  exportArrivalsCsvInBackground = () => {
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef: this.props.locationRef,
            option: this.getOption(),
            dateFrom: formatDateForApi(this.props.filter.dateFrom),
            dateTo: formatDateForApi(this.props.filter.dateTo)
          },
          exportSdk: exportArrivalsCsvInBackgroundSdk,
          fileName: ARRIVALS_CSV_FILENAME
        })
    );
  };

  openBookingNotesModal = (bookingId: any) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_BOOKING_NOTES,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.BOOKING_NOTES, {
          bookingId,
          locationRef,
          refetchExtraAction: this.fetchArrivalsExtraActions
        });
      }
    );
  };

  componentDidMount() {
    this.fetchArrivals();
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter.value !== prevProps.filter.value && !this.isToday()) {
      this.setState({isOpened: false});
    }

    if (
      this.props.filter.value !== prevProps.filter.value ||
      this.props.filter.dateFrom !== prevProps.filter.dateFrom ||
      this.props.filter.dateTo !== prevProps.filter.dateTo
    ) {
      this.fetchArrivals();
    }

    const arrivalsAreFetched = !_.isEqual(
      prevProps.arrivals,
      this.props.arrivals
    );
    const arrivalsForTodayAreFetched = !_.isEqual(
      prevProps.checkedInTodayArrivals,
      this.props.checkedInTodayArrivals
    );

    if (this.props.arrivalsLoading === false) {
      this.debouncer();
    }

    if (arrivalsAreFetched || arrivalsForTodayAreFetched) {
      ReactTooltip.rebuild();
    }
  }

  rebuildTooltips() {
    ReactTooltip.rebuild();
  }

  // Since we only sort by arrivalDate at the moment it's ok to hardcode the sort field name
  getSortedTableData = data => {
    const sort = this.props.getSort();

    if (sort.field === 'arrivalDate' && sort.direction === '+') {
      return _.sortBy(data, [
        el => moment(el.arrivalDate, DATE_FORMAT_API).unix()
      ]);
    }

    if (sort.field === 'arrivalDate' && sort.direction === '-') {
      return _.sortBy(data, [
        el => -moment(el.arrivalDate, DATE_FORMAT_API).unix()
      ]);
    }

    return data;
  };

  redirectToBookingSet = ({bookingSetId}) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(
          reverse(BOOKING_SET_DETAIL_URL, {locationRef, bookingSetId})
        );
      }
    );
  };

  undoCheckInTenants = bookingId => {
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo check in member',
      question: 'Are you sure you want to undo this check in?',
      confirmAction: () =>
        this.props.undoCheckInTenants({bookingId}, [
          ...this.fetchArrivalsExtraActions(),
          () => closeModal(BASE_MODALS.CONFIRM)
        ])
    });
  };

  fetchTooltipData = data =>
    !_.isEmpty(data) && (
      <BookingSetBalance
        bookingSetRentAndOneOffBalance={data.bookingSetRentAndOneOffBalance}
        bookingSetDepositBalance={data.bookingSetDepositBalance}
      />
    );

  render() {
    const {isOpened} = this.state;
    const {
      arrivalsLoading,
      checkedInTodayArrivalsLoading,
      exportingCsv,
      modals: {isMemberCheckInModalOpen}
    } = this.props;

    const arrivals = this.getSortedTableData(this.props.arrivals);
    const checkedInTodayArrivals = this.getSortedTableData(
      this.props.checkedInTodayArrivals
    );

    return (
      <div className="ArrivalsTable">
        <div className="heading">
          <div className="label">Arrivals</div>
        </div>
        <Table>
          <Table.Header>
            <Table.HeaderItem heading="name" className="name" />
            <Table.HeaderItem heading="type" className="type" />
            <Table.HeaderItem
              heading="arrival"
              className="arrival"
              sortDirection={
                this.props.getSort().field === 'arrivalDate' &&
                this.props.getSort().direction
              }
              onSort={() => this.props.onSort('arrivalDate')}
            />
            <Table.HeaderItem heading="departure" className="departure" />
            <Table.HeaderItem heading="room no" className="room" />
            <Table.HeaderItem heading="room type" className="roomType" />
            <Table.HeaderItem heading="owed" className="balance" />
            <Table.HeaderItem heading="notes" className="notesCount" />
            <Table.HeaderItem heading="inspected" className="inspected" />
            <Table.HeaderItem heading="agreement" className="agreementSigned" />
            <Table.HeaderItem heading="status" className="status" />
            <Table.HeaderItem heading="check-in" className="checkIn" />
          </Table.Header>
        </Table>
        <div className="arrivals">
          {arrivalsLoading && <Loading />}
          {!arrivalsLoading && _.isEmpty(arrivals) && <EmptyListPlaceholder />}
          {!arrivalsLoading &&
            !_.isEmpty(arrivals) && (
              <Table>
                <Table.Body>
                  {arrivals.map(booking => (
                    <ArrivalRow
                      key={booking.bookingId.toString()}
                      openMemberCheckInModal={this.openMemberCheckInModal}
                      openRoomInspectionModal={
                        booking.roomInspectionPassed
                          ? this.openRoomInspectionRemoveModal
                          : this.openRoomInspectionPassModal
                      }
                      data={booking}
                      isToday={this.isToday()}
                      openBookingNotesModal={this.openBookingNotesModal}
                      redirectToBookingSet={this.redirectToBookingSet}
                    />
                  ))}
                </Table.Body>
              </Table>
            )}
        </div>
        <TableSplitter
          isOpened={isOpened}
          showSplitButton={this.isToday()}
          triggerShow={this.showCheckedIn}
          triggerHide={this.hideCheckedIn}
          splitButtonText="Checked in"
          exportDisabled={
            arrivalsLoading || exportingCsv || _.isEmpty(arrivals)
          }
          exportButtonTitle="Export arrivals"
          triggerExport={this.exportArrivalsCsv}
          triggerExperimentalExport={this.exportArrivalsCsvInBackground}
        />
        {isOpened && (
          <div className="checkedInToday">
            {checkedInTodayArrivalsLoading && <Loading />}
            {!checkedInTodayArrivalsLoading &&
              _.isEmpty(checkedInTodayArrivals) && <EmptyListPlaceholder />}
            {!checkedInTodayArrivalsLoading &&
              !_.isEmpty(checkedInTodayArrivals) && (
                <Table>
                  <Table.Body>
                    {checkedInTodayArrivals.map(booking => (
                      <ArrivalRow
                        key={booking.bookingId.toString()}
                        openRoomInspectionModal={
                          booking.roomInspectionPassed
                            ? this.openRoomInspectionRemoveModal
                            : this.openRoomInspectionPassModal
                        }
                        openBookingNotesModal={this.openBookingNotesModal}
                        data={booking}
                        redirectToBookingSet={this.redirectToBookingSet}
                        undoCheckInTenants={this.undoCheckInTenants}
                      />
                    ))}
                  </Table.Body>
                </Table>
              )}
          </div>
        )}
        {isMemberCheckInModalOpen && (
          <MemberCheckInModal
            getExtraActions={this.fetchArrivalsExtraActions}
          />
        )}

        <Tooltip
          place="left"
          effect="solid"
          id={TOOLTIP_NAME}
          getContent={tooltipData => {
            const data = tooltipData && JSON.parse(tooltipData);
            const content =
              data && data.type === 'bookingType'
                ? data.content
                : this.fetchTooltipData(data);
            return content;
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  exportingCsv: selectExporting(state, ARRIVALS_CSV_FILENAME),
  arrivals: selectArrivals(state),
  arrivalsLoading: selectArrivalsLoading(state),
  checkedInTodayArrivals: selectCheckedInTodayArrivals(state),
  checkedInTodayArrivalsLoading: selectCheckedInTodayArrivalsLoading(state),
  modals: {
    isMemberCheckInModalOpen: isModalOpen(state, MODALS.MEMBER_CHECK_IN)
  }
});

const mapDispatchToProps = {
  fetchArrivals,
  fetchCheckedInTodayArrivals,
  openModal,
  requiresPermission,
  exportCsv,
  exportCsvInBackground,
  undoCheckInTenants
};

export default withSorting(
  connect(mapStateToProps, mapDispatchToProps)(ArrivalsTable),
  SORTING.ARRIVALS_TABLE
) as React.ComponentType<ArrivalsTableProps>;
